import { isPlainObject, isEqual } from 'lodash-es';
import { capitalize } from './filters.mjs';

export function isEmpty(object) {
    return Object.keys(object).length === 0;
}

/**
 * @param {object} object An object
 * @param {number} depth Max depth level to check. Zero is root
 * @returns {boolean}
 * @example c returns true
 * @example allKeysEmpty({a: '', b: {c: {d: ''}}}, 0) returns false
 * @description Use `depth` parameter with them `Date()`. Note that `null` is an object.
 */
export function allKeysEmpty(object, depth = Infinity) {
    if (!object || typeof object !== 'object' || depth < 0) return null;

    return !Object.keys(object).some(key =>
        typeof object[key] === 'object' && depth > 0
            ? !allKeysEmpty(object[key], depth - 1)
            : !!object[key]);
}

/**
 * @param {boolean} condition condition to add an item to an object/array
 * @param {array|object} item
 * @returns {array}
 *
 * @example
 * // returns {a: 1, b: 2, c: 3}
 * const x = { a: 1, b: 2, ...dsc.object.conditionalItems(true, [{ c: 3 }]) }
 * @example
 * // returns [1, 2, 3]
 * const y = [ 1, 2, 3, ...dsc.object.conditionalItems(false, [ 4 ])}
 */
export function conditionalItems(condition, item) {
    return condition ? item : [];
}

/**
 * @param {object} object
 * @param {string} key
 * @param {*} value
 * @returns {array} array of objects that contain 'key: val' pair
 * @example getAllWithPair({x: {a: 1, b: 2}, y: {a: 1, b: 3}}, 'b', 3) returns [{a: 1, b: 3}]
 */
export function getAllWithPair(object, key, value) {
    let objects = [];

    for (const i in object) {
        if (!Object.hasOwn(object, i)) continue;
        if (typeof object[i] === 'object') {
            objects = objects.concat(getAllWithPair(object[i], key, value));
        } else if (i === key && object[i] === value || i === key && value === '') {
            objects.push(object);
        } else if (object[i] === value && key === ''){
            if (objects.lastIndexOf(object) === -1){
                objects.push(object);
            }
        }
    }

    return objects;
}

/**
 * @param {object} object An object
 * @param {boolean} unique generate unique key names
 * @param {string} prefix a prefix for key name, works with `unique` key
 * @returns {object}
 * @example flatten({a: 1, b: {c: {d: {eeva: 2, kaapo: 3}}, f: {g: 4, h: { iina: 5 }}}}) returns { a: 1, bcdEeva: 2, bcdKaapo: 3, bfG: 4, bfhIina: 5 }
 * @example flatten({a: 1, b: { a: 2 }}) returns { a: 1, bA: 2 }
 * @example flatten({a: 1, b: { a: 2 }}, false) returns { a: 2 }
 */
export function flatten(object, unique = true, prefix = '') {
    return Object.keys(object).reduce((acc, k) => {
        if (!!object[k] && object[k].constructor === Object) {
            Object.assign(acc, flatten(object[k], unique, `${prefix}${k}`));
        } else {
            const keyName = unique ? `${prefix}${prefix ? capitalize(k) : k}` : k;
            acc[keyName] = object[k];
        }

        return acc;
    }, {});
}

export function isObject(x) {
    return isPlainObject(x);
}

/**
 * @remarks Arrays are shallowly compared.
 * @param {array} obj1 An original object or array
 * @param {array} obj2 An object to compare with the original object or array
 * @param {boolean} strictOrder If array, take order of array elements into account
 * @returns {boolean}
 */
export function same(obj1, obj2, strictOrder = false) {
    if (strictOrder) return JSON.stringify(obj1) === JSON.stringify(obj2);
    if (isObject(obj1) && isObject(obj2)) return isEqual(obj1, obj2);
    if (Array.isArray(obj1) && Array.isArray(obj2)) return JSON.stringify([...obj1].sort()) === JSON.stringify([...obj2].sort());

    return false;
}

/**
 * @param {object} obj An object with key to rename
 * @param {string} oldKey A key in obj to rename
 * @param {string} newKey A new key name for oldKey
  * @returns {object} Modified object obj
 */
export function mvKey(obj = {}, oldKey, newKey) {
    if (obj[oldKey]) delete Object.assign(obj, { [newKey]: obj[oldKey] })[oldKey];

    return obj;
}
